import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

type TouchGhost = null | {
  id: string
  x: number
  y: number
  w: number
  h: number
  ox: number
  oy: number
}

type CanStartDrag = (target: HTMLElement | null) => boolean

const defaultCanStartDrag: CanStartDrag = (target) => {
  if (!target) return true
  if (target.closest?.("[data-reorder-no-drag]")) return false
  if (target.closest?.("[data-swapy-no-drag]")) return false
  if (target.closest?.("button,a,input,textarea,select,[role='button']")) return false
  return true
}

export function useSwapReorder<T>(args: {
  items: T[]
  setItems: React.Dispatch<React.SetStateAction<T[]>>
  getId: (item: T) => string
  canStartDrag?: CanStartDrag
  onReorder?: (nextItems: T[]) => void
  touchGhost?: boolean
}) {
  const { items, setItems, getId, canStartDrag = defaultCanStartDrag, onReorder, touchGhost = true } = args

  const containerRef = useRef<HTMLDivElement>(null)
  const rectsRef = useRef<Map<string, DOMRect>>(new Map())
  const [flipTick, setFlipTick] = useState(0)

  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)
  const dragItemRef = useRef<number | null>(null)
  const touchCurrentIndex = useRef<number | null>(null)

  const [ghost, setGhost] = useState<TouchGhost>(null)
  const ghostRef = useRef<TouchGhost>(null)
  ghostRef.current = ghost

  const touchRafRef = useRef<number | null>(null)
  const onReorderRef = useRef<typeof onReorder>(onReorder)
  onReorderRef.current = onReorder

  const scrollLockRef = useRef<null | {
    el: HTMLElement
    prevTouchAction: string
    prevOverscrollBehavior: string
    cleanup: () => void
  }>(null)

  const lastTouchClientYRef = useRef<number | null>(null)
  const autoScrollRafRef = useRef<number | null>(null)

  const findScrollContainer = useCallback((start: HTMLElement | null): HTMLElement | null => {
    if (typeof window === "undefined") return null
    let node: HTMLElement | null = start
    while (node) {
      const style = window.getComputedStyle(node)
      const overflowY = style.overflowY
      const scrollableY = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight
      if (scrollableY) return node
      node = node.parentElement
    }
    const scrollingElement = document.scrollingElement
    return scrollingElement instanceof HTMLElement ? scrollingElement : null
  }, [])

  const lockScrollDuringTouchDrag = useCallback(() => {
    if (scrollLockRef.current) return
    const root = containerRef.current
    if (!root) return
    const scrollEl = findScrollContainer(root)
    if (!scrollEl) return

    const prevTouchAction = scrollEl.style.touchAction
    const prevOverscrollBehavior = (scrollEl.style as any).overscrollBehavior || ""

    const prevent = (ev: TouchEvent) => {
      ev.preventDefault()
    }

    scrollEl.addEventListener("touchmove", prevent, { passive: false })
    scrollEl.style.touchAction = "none"
    ;(scrollEl.style as any).overscrollBehavior = "contain"

    scrollLockRef.current = {
      el: scrollEl,
      prevTouchAction,
      prevOverscrollBehavior,
      cleanup: () => {
        scrollEl.removeEventListener("touchmove", prevent as any)
        scrollEl.style.touchAction = prevTouchAction
        ;(scrollEl.style as any).overscrollBehavior = prevOverscrollBehavior
      },
    }
  }, [findScrollContainer])

  const unlockScrollAfterTouchDrag = useCallback(() => {
    if (!scrollLockRef.current) return
    try {
      scrollLockRef.current.cleanup()
    } finally {
      scrollLockRef.current = null
    }
  }, [])

  const tickAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current !== null) return
    autoScrollRafRef.current = requestAnimationFrame(() => {
      autoScrollRafRef.current = null
      const y = lastTouchClientYRef.current
      const lock = scrollLockRef.current
      if (y === null || !lock) return

      const el = lock.el
      const rect = el.getBoundingClientRect()
      const threshold = 84
      const maxSpeed = 18

      let dy = 0
      if (y < rect.top + threshold) {
        const t = Math.max(0, rect.top + threshold - y) / threshold
        dy = -Math.ceil(maxSpeed * t)
      } else if (y > rect.bottom - threshold) {
        const t = Math.max(0, y - (rect.bottom - threshold)) / threshold
        dy = Math.ceil(maxSpeed * t)
      }

      if (dy !== 0) {
        const prev = el.scrollTop
        el.scrollTop = prev + dy
        if (el.scrollTop !== prev) tickAutoScroll()
      }
    })
  }, [])

  const captureRects = useCallback(() => {
    if (!containerRef.current) return
    const map = new Map<string, DOMRect>()
    containerRef.current.querySelectorAll<HTMLElement>("[data-reorder-id]").forEach((el) => {
      const id = el.getAttribute("data-reorder-id")
      if (id) map.set(id, el.getBoundingClientRect())
    })
    rectsRef.current = map
    setFlipTick((t) => t + 1)
  }, [])

  useLayoutEffect(() => {
    if (!containerRef.current || rectsRef.current.size === 0) return
    const oldRects = rectsRef.current

    containerRef.current.querySelectorAll<HTMLElement>("[data-reorder-id]").forEach((el) => {
      const id = el.getAttribute("data-reorder-id")
      if (!id) return
      const oldRect = oldRects.get(id)
      if (!oldRect) return

      const newRect = el.getBoundingClientRect()
      const dx = oldRect.left - newRect.left
      const dy = oldRect.top - newRect.top
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return

      el.style.transition = "none"
      el.style.transform = `translate(${dx}px, ${dy}px)`

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transition = "transform 300ms cubic-bezier(0.2, 0, 0, 1)"
          el.style.transform = ""
          const cleanup = () => {
            el.style.transition = ""
            el.removeEventListener("transitionend", cleanup)
          }
          el.addEventListener("transitionend", cleanup)
        })
      })
    })

    rectsRef.current = new Map()
  }, [items, flipTick])

  const swapItems = useCallback(
    (from: number, to: number) => {
      if (from === to) return
      captureRects()
      setItems((prev) => {
        const next = [...prev]
        const temp = next[from]
        next[from] = next[to]
        next[to] = temp
        onReorderRef.current?.(next)
        return next
      })
    },
    [captureRects, setItems]
  )

  const stopDragging = useCallback(() => {
    dragItemRef.current = null
    touchCurrentIndex.current = null
    setDragIndex(null)
    setOverIndex(null)
    setGhost(null)
    containerRef.current?.closest("[data-reorder-container]")?.classList.remove("overflow-hidden")
    unlockScrollAfterTouchDrag()
    lastTouchClientYRef.current = null
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current)
      autoScrollRafRef.current = null
    }
    if (touchRafRef.current !== null) {
      cancelAnimationFrame(touchRafRef.current)
      touchRafRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (touchRafRef.current !== null) {
        cancelAnimationFrame(touchRafRef.current)
        touchRafRef.current = null
      }
      if (autoScrollRafRef.current !== null) {
        cancelAnimationFrame(autoScrollRafRef.current)
        autoScrollRafRef.current = null
      }
      unlockScrollAfterTouchDrag()
    }
  }, [])

  const handleContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!containerRef.current) return
      if (dragItemRef.current === null) return
      const target = document
        .elementFromPoint(e.clientX, e.clientY)
        ?.closest?.("[data-reorder-id]") as HTMLElement | null
      if (!target) return
      const id = target.getAttribute("data-reorder-id")
      if (!id) return
      const idx = items.findIndex((p) => getId(p) === id)
      if (idx < 0) return
      setOverIndex(idx)
    },
    [items, getId]
  )

  const handleContainerDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (dragItemRef.current === null || overIndex === null) return
      swapItems(dragItemRef.current, overIndex)
      stopDragging()
    },
    [overIndex, swapItems, stopDragging]
  )

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (!canStartDrag(e.target as HTMLElement | null)) {
        e.preventDefault()
        stopDragging()
        return
      }
      dragItemRef.current = index
      setDragIndex(index)
      if (!e.dataTransfer) return
      const id = items[index] ? getId(items[index]) : null
      if (id) {
        e.dataTransfer.effectAllowed = "move"
        e.dataTransfer.setData("text/plain", id)
      }
      const el = e.currentTarget as HTMLElement
      const clone = el.cloneNode(true) as HTMLElement
      clone.style.position = "absolute"
      clone.style.top = "-10000px"
      clone.style.left = "-10000px"
      clone.style.width = `${el.getBoundingClientRect().width}px`
      clone.style.height = `${el.getBoundingClientRect().height}px`
      clone.style.transform = "scale(1.02)"
      clone.style.opacity = "0.98"
      document.body.appendChild(clone)
      const rect = clone.getBoundingClientRect()
      e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2)
      requestAnimationFrame(() => clone.remove())
    },
    [items, getId, canStartDrag, stopDragging]
  )

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (dragItemRef.current === null) return
    setOverIndex(index)
  }, [])

  const handleDrop = useCallback(
    (index: number) => {
      if (dragItemRef.current === null) return
      swapItems(dragItemRef.current, index)
      stopDragging()
    },
    [swapItems, stopDragging]
  )

  const handleDragEnd = useCallback(() => {
    stopDragging()
  }, [stopDragging])

  const handleTouchStart = useCallback(
    (index: number, e: React.TouchEvent) => {
      if (!touchGhost) return
      if (!canStartDrag(e.target as HTMLElement | null)) return
      touchCurrentIndex.current = index
      setDragIndex(index)
      lockScrollDuringTouchDrag()
      containerRef.current?.closest("[data-reorder-container]")?.classList.add("overflow-hidden")
      const touchPoint = e.touches[0]
      lastTouchClientYRef.current = touchPoint?.clientY ?? null
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const item = items[index]
      if (item) {
        setGhost({
          id: getId(item),
          x: touchPoint.clientX - (touchPoint.clientX - rect.left),
          y: touchPoint.clientY - (touchPoint.clientY - rect.top),
          w: rect.width,
          h: rect.height,
          ox: touchPoint.clientX - rect.left,
          oy: touchPoint.clientY - rect.top,
        })
      }
    },
    [items, getId, canStartDrag, touchGhost, lockScrollDuringTouchDrag]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchGhost) return
      if (touchCurrentIndex.current === null || !containerRef.current) return
      e.preventDefault()
      const touchPoint = e.touches[0]
      lastTouchClientYRef.current = touchPoint?.clientY ?? null
      tickAutoScroll()
      const g = ghostRef.current
      if (g) {
        if (touchRafRef.current === null) {
          touchRafRef.current = requestAnimationFrame(() => {
            touchRafRef.current = null
            const current = ghostRef.current
            const t = e.touches[0]
            if (!current || !t) return
            setGhost((prev) => {
              if (!prev) return prev
              return { ...prev, x: t.clientX - prev.ox, y: t.clientY - prev.oy }
            })
          })
        }
      }

      const elements = containerRef.current.querySelectorAll<HTMLElement>("[data-reorder-id]")
      let hoveredIndex: number | null = null
      elements.forEach((el, i) => {
        const rect = el.getBoundingClientRect()
        if (
          touchPoint.clientX >= rect.left &&
          touchPoint.clientX <= rect.right &&
          touchPoint.clientY >= rect.top &&
          touchPoint.clientY <= rect.bottom
        ) {
          hoveredIndex = i
        }
      })
      if (hoveredIndex !== null) setOverIndex(hoveredIndex)
    },
    [touchGhost, tickAutoScroll]
  )

  const handleTouchEnd = useCallback(() => {
    if (!touchGhost) return
    if (touchCurrentIndex.current !== null && overIndex !== null) {
      swapItems(touchCurrentIndex.current, overIndex)
    }
    stopDragging()
  }, [overIndex, swapItems, stopDragging, touchGhost])

  const getItemProps = useCallback(
    (index: number) => {
      const id = items[index] ? getId(items[index]) : ""
      return {
        "data-reorder-id": id,
        draggable: true,
        onDragStart: (e: React.DragEvent) => handleDragStart(e, index),
        onDragOver: (e: React.DragEvent) => handleDragOver(e, index),
        onDrop: () => handleDrop(index),
        onDragEnd: handleDragEnd,
        onTouchStart: (e: React.TouchEvent) => handleTouchStart(index, e),
      }
    },
    [items, getId, handleDragStart, handleDragOver, handleDrop, handleDragEnd, handleTouchStart]
  )

  return {
    containerRef,
    containerProps: {
      onDragOver: handleContainerDragOver,
      onDrop: handleContainerDrop,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    getItemProps,
    dragIndex,
    overIndex,
    isDragging: dragIndex !== null,
    touchGhost: ghost,
  }
}

